import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Shield, User, AlertCircle, CheckCircle, Key } from 'lucide-react';

const AdminPage = ({ onNavigateToStock }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user'
  });
  const [passwordEdit, setPasswordEdit] = useState({
    userId: null,
    newPassword: '',
    confirmPassword: '',
    loading: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users', {
        credentials: 'include'
      });

      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
        setError('');
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.username.trim() || !newUser.password.trim()) {
      setError('Username and password are required');
      return;
    }

    if (newUser.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setCreateLoading(true);
      setError('');
      
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        const createdUser = await response.json();
        setUsers(prev => [createdUser, ...prev]);
        setNewUser({ username: '', password: '', role: 'user' });
        setShowCreateForm(false);
        setSuccess(`User "${createdUser.username}" created successfully`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setUsers(prev => prev.filter(user => user.id !== userId));
        setSuccess(`User "${username}" deleted successfully`);
        setTimeout(() => setSuccess(''), 3000);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const handleUpdateRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const user = users.find(u => u.id === userId);
    
    if (!window.confirm(`Change ${user.username}'s role from ${currentRole} to ${newRole}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        setUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        ));
        setSuccess(`User "${user.username}" role updated to ${newRole}`);
        setTimeout(() => setSuccess(''), 3000);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update user role');
      }
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordEdit.newPassword !== passwordEdit.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordEdit.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setPasswordEdit(prev => ({ ...prev, loading: true }));
      setError('');

      const response = await fetch(`/api/users/${passwordEdit.userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ password: passwordEdit.newPassword })
      });

      if (response.ok) {
        const user = users.find(u => u.id === passwordEdit.userId);
        setSuccess(`Password updated successfully for user "${user.username}"`);
        setPasswordEdit({
          userId: null,
          newPassword: '',
          confirmPassword: '',
          loading: false
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update password');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Failed to update password');
    } finally {
      setPasswordEdit(prev => ({ ...prev, loading: false }));
    }
  };

  const startPasswordEdit = (userId) => {
    setPasswordEdit({
      userId,
      newPassword: '',
      confirmPassword: '',
      loading: false
    });
    setError('');
  };

  const cancelPasswordEdit = () => {
    setPasswordEdit({
      userId: null,
      newPassword: '',
      confirmPassword: '',
      loading: false
    });
    setError('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-title">
            <Users size={28} />
            <h1>User Management</h1>
          </div>
        </div>
        <div className="loading-spinner">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-title">
          <Users size={28} />
          <h1>User Management</h1>
        </div>
        <div className="admin-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus size={18} />
            Create User
          </button>
          <button 
            className="btn-secondary"
            onClick={onNavigateToStock}
          >
            Stock Dashboard
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {showCreateForm && (
        <div className="create-user-form">
          <h3>Create New User</h3>
          <form onSubmit={handleCreateUser}>
            <div className="form-row">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter password (min 6 characters)"
                  minLength="6"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-primary"
                disabled={createLoading}
              >
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewUser({ username: '', password: '', role: 'user' });
                  setError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {passwordEdit.userId && (
        <div className="password-edit-form">
          <h3>Change Password for {users.find(u => u.id === passwordEdit.userId)?.username}</h3>
          <form onSubmit={handlePasswordChange}>
            <div className="form-row">
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordEdit.newPassword}
                  onChange={(e) => setPasswordEdit(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password (min 6 characters)"
                  minLength="6"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={passwordEdit.confirmPassword}
                  onChange={(e) => setPasswordEdit(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  minLength="6"
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-primary"
                disabled={passwordEdit.loading}
              >
                {passwordEdit.loading ? 'Updating...' : 'Update Password'}
              </button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={cancelPasswordEdit}
                disabled={passwordEdit.loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-table">
        <h3>All Users ({users.length})</h3>
        {users.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>No users found</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      {user.role === 'admin' ? <Shield size={16} /> : <User size={16} />}
                      {user.username}
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="user-actions">
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => startPasswordEdit(user.id)}
                        title="Change password"
                      >
                        <Key size={14} />
                      </button>
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => handleUpdateRole(user.id, user.role)}
                        title={`Change role to ${user.role === 'admin' ? 'user' : 'admin'}`}
                      >
                        {user.role === 'admin' ? 'Make User' : 'Make Admin'}
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          className="btn-small btn-danger"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          title="Delete user"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminPage;